import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { Play, Star, X, ArrowRight } from "lucide-react";

// Shared marketing assets used across the homepage, the $1-trial landing pages,
// and the public SEO pages. Single source of truth for the video case studies,
// client wins, the video lightbox, and the reusable "$1 trial" CTAs.

export const VIDEO_TESTIMONIALS = [
  {
    name: "Bianca",
    industry: "Bail Bonds, Florida",
    headline: "3X'd Google Maps Visibility in Under 4 Months",
    result: "200% Maps Visibility Surge",
    vimeoId: "1143295236",
    thumbnail: "https://i.vimeocdn.com/video/2091427506-bd802fc06dc2bfe29d4272a7a59791ec41fa243fa1cce08ddd0116a730708eb1-d?mw=600&q=85",
    quote: "Bianca runs one of Florida’s biggest bail bond companies and plugged into Maps Domination to own the state, county by county—cranking up Monroe, eyeing Polk next, and rolling the extra cash into new locations so she can build a legacy business for her daughter.",
    url: "https://digitaldomination.ai/bail-bonds-testimonial-case-study/",
  },
  {
    name: "Mark",
    industry: "Eyeglasses, Pensacola",
    headline: "Map Pack Monopolization in 7 Weeks",
    result: "700% Growth for Main Target Keyword",
    vimeoId: "1140420130",
    thumbnail: "https://i.vimeocdn.com/video/2087431083-825dae075099bcd43f55813e0be9c8f92f33d5cee066caa7ae3db3b6e729b5f7-d?mw=600&q=85",
    quote: "Mark went from “just another eye doc business” to the 2,400-pound gorilla of the Gulf Coast—dominating search, packing his schedule.",
    url: "https://digitaldomination.ai/marks-eyeglass-testimonial-case-study/",
  },
  {
    name: "Josh",
    industry: "Family-Owned Business",
    headline: "From Invisible to Selling to Private Equity",
    result: "Top 3 on Google Maps in 8 Weeks",
    vimeoId: "1139093793",
    thumbnail: "https://i.vimeocdn.com/video/2085448257-662dda97c6d3d849a0c021ab3f1f240fff5a965261be5e3c24c509efea7a1782-d?mw=600&q=85",
    quote: "Josh wasn’t in the top 3 on Google Maps—he didn’t exist. Digital Domination ranked him in the Top 3 and flooded him with new customers in less than 8 weeks.",
    url: "https://digitaldomination.ai/joshs-business-transformation/",
  },
  {
    name: "Simply Health",
    industry: "Medical Center",
    headline: "From Unpredictable Calls to Daily Patient Growth",
    result: "Consistent Patient Growth via Maps",
    vimeoId: "1143008329",
    thumbnail: "https://i.vimeocdn.com/video/2091032070-a926df1ea944d0575f1a434f754968f54b08b0ee4d423f422fd6da4c9c8ec150-d?mw=600&q=85",
    quote: "Simply Health unlocked consistent patient growth by winning the map and turning intent into booked appointments.",
    url: "https://digitaldomination.ai/medical-center-testimonial-case-study/",
  },
  {
    name: "Medina Auto Group",
    industry: "Car Dealership, 4 Locations",
    headline: "Deleted 89 Unfair 1-Star Reviews & Took Over Google Maps",
    result: "Dominating Every High-Intent Search",
    vimeoId: "1144736149",
    thumbnail: "https://i.vimeocdn.com/video/2093428261-67f4e90e47562cf621b048d1f328ecaabad0b72d841f9ce8272e0fae1bf6bbd8-d?mw=600&q=85",
    quote: "Medina Auto Group wiped out 89 unfair 1-star reviews and took over Google Maps for every major buyer search—in record time.",
    url: "https://digitaldomination.ai/car-dealership-testimonial-case-study/",
  },
  {
    name: "California HVAC Co.",
    industry: "HVAC Contractor, California",
    headline: "$746,635 in a Single Month",
    result: "223.74% YoY Revenue Surge",
    vimeoId: "1143003880",
    thumbnail: "https://i.vimeocdn.com/video/2091025391-15323566482f4d4e972039b12c1a672519f26b193ad41d68d3629d5b6568d62c-d?mw=600&q=85",
    quote: "This California HVAC company hit $746,635 in a single month using Digital Domination—a 223.74% year-over-year revenue surge powered by advanced Google Maps optimization.",
    url: "https://digitaldomination.ai/california-hvac-contractor-case-study/",
  },
];

export const CLIENT_WINS = [
  {
    name: "Alex",
    industry: "Barbershop, Las Vegas",
    quote: "In 5 months with Maps Domination, Alex’s new Las Vegas barbershop went from “who are you?” to a 640% increase in market share—owning the map, stealing market share from top competitors who had been in business for decades. His shop is booked solid, and he’s now rolling profits into a second location.",
    result: "640% Market Share Increase",
  },
  {
    name: "Daniel",
    industry: "Local Business, Nashville",
    quote: "Daniel plugged into Maps Domination and felt it working instantly—5 campaign win updates in the first 48 hours. Within 2 months, the phones wouldn’t stop ringing, he’s hiring more staff, and now he’s scouting new locations to handle the demand.",
    result: "Phones Won't Stop Ringing",
  },
  {
    name: "Bianca",
    industry: "Bail Bonds, Florida",
    quote: "Bianca runs one of Florida’s biggest bail bond companies and plugged into Maps Domination to own the state, county by county—cranking up Monroe, eyeing Polk next, and rolling the extra cash into new locations so she can build a legacy business for her daughter.",
    result: "Dominating County by County",
  },
  {
    name: "Mark",
    industry: "Eye Glasses, Pensacola",
    quote: "Mark plugged into Maps Domination and went from “just another eye doc business” to the 2,400-pound gorilla of the Gulf Coast—dominating search, packing his schedule.",
    result: "700% Keyword Growth",
  },
];

const EXPLAINER_VIDEOS = [
  { vimeoId: "1122072569", title: "Digital Domination Overview" },
  { vimeoId: "1122072587", title: "How Digital Domination Works" },
  { vimeoId: "1122072611", title: "GeoGrid Scanning Explained" },
  { vimeoId: "1122072802", title: "Maps Domination Results" },
];

export function VideoModal({ vimeoId, onClose }: { vimeoId: string; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="video-modal">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-4xl mx-4 aspect-video">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/60 hover:text-white transition-colors"
          data-testid="button-close-video-modal"
        >
          <X className="w-6 h-6" />
        </button>
        <iframe
          src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0`}
          className="w-full h-full rounded-xl"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}

// The single reusable "$1 trial" button. Points at the existing signup flow
// (/login → Google sign-in) which is where every CTA on the marketing site
// funnels. Lead-source attribution is stashed on the /trial landing mount.
export function TrialButton({
  label = "Start $1 Trial",
  testid,
  className = "",
}: {
  label?: string;
  testid?: string;
  className?: string;
}) {
  return (
    <Link href="/login">
      <Button
        className={`bg-[#F6642A] hover:bg-[#F6642A]/90 text-white font-semibold h-13 px-8 text-base ${className}`}
        data-testid={testid}
      >
        {label}
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </Link>
  );
}

// A compact, centered "$1 trial" call-to-action dropped at the end of a section.
export function SectionCTA({
  body = "$1 for 3 days, then your plan price. 10x the value or cancel in one click.",
  testid = "cta-section",
}: {
  body?: string;
  testid?: string;
}) {
  return (
    <div className="mt-14 text-center" data-testid={testid}>
      <div className="flex justify-center">
        <TrialButton testid={`${testid}-button`} />
      </div>
      <p className="text-xs text-gray-500 mt-3">{body}</p>
    </div>
  );
}

// Video case studies + client wins. Toggle the stat row and explainer videos
// so the homepage shows the full block while trial/SEO pages show a tighter one.
export function TestimonialsSection({
  showStats = true,
  showExplainers = true,
  heading = "Dominate Google Maps in 12 Weeks — Or It's Free",
  subhead = "Real businesses. Real transformations. Guaranteed results.",
}: {
  showStats?: boolean;
  showExplainers?: boolean;
  heading?: string;
  subhead?: string;
}) {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const stats = [
    { value: "5,000+", label: "Local businesses served across the US" },
    { value: "$746K", label: "Single-month revenue record by one HVAC client" },
    { value: "640%", label: "Market share increase for a Las Vegas barbershop" },
  ];

  return (
    <section className="py-20 px-6 relative" data-testid="section-social-proof">
      {activeVideo && <VideoModal vimeoId={activeVideo} onClose={() => setActiveVideo(null)} />}
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F6642A]">REAL RESULTS</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-4" data-testid="text-proof-h2">
            {heading}
          </h2>
          <p className="text-gray-400 mt-3 max-w-2xl mx-auto">{subhead}</p>
        </div>

        {showStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {stats.map((s, i) => (
              <div key={i} className="p-6 sm:p-8 rounded-2xl bg-[#12121A] border border-[#1E1E2E] text-center" data-testid={`card-stat-${i}`}>
                <div className="text-3xl sm:text-4xl font-bold text-[#F6642A] mb-2">{s.value}</div>
                <p className="text-sm text-gray-400">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-testid="text-case-studies-heading">
            <Play className="w-5 h-5 text-[#F6642A]" /> Video Case Studies
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VIDEO_TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="rounded-2xl bg-[#12121A] border border-[#1E1E2E] overflow-hidden hover:border-[#F6642A]/30 transition-colors group cursor-pointer"
                onClick={() => setActiveVideo(t.vimeoId)}
                data-testid={`card-video-testimonial-${i}`}
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={t.thumbnail}
                    alt={t.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-[#F6642A] flex items-center justify-center shadow-lg shadow-[#F6642A]/30 group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-white fill-white ml-0.5" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 left-2">
                    <span className="text-[10px] font-bold bg-[#F6642A] text-white px-2 py-0.5 rounded-full">{t.result}</span>
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="text-sm font-bold text-white mb-1 line-clamp-2">{t.headline}</h4>
                  <p className="text-xs text-gray-400">{t.name} — {t.industry}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {showExplainers && (
          <div className="mt-14 mb-14">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-testid="text-explainer-heading">
              <Play className="w-5 h-5 text-[#0081DF]" /> See How It Works
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {EXPLAINER_VIDEOS.map((v, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-[#12121A] border border-[#1E1E2E] overflow-hidden hover:border-[#0081DF]/30 transition-colors group cursor-pointer"
                  onClick={() => setActiveVideo(v.vimeoId)}
                  data-testid={`card-explainer-video-${i}`}
                >
                  <div className="relative aspect-video overflow-hidden bg-black/50 flex items-center justify-center">
                    <iframe
                      src={`https://player.vimeo.com/video/${v.vimeoId}?badge=0&autopause=0&player_id=0`}
                      className="w-full h-full pointer-events-none"
                      allow="autoplay; fullscreen"
                      title={v.title}
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-[#0081DF] flex items-center justify-center shadow-lg shadow-[#0081DF]/30 group-hover:scale-110 transition-transform">
                        <Play className="w-6 h-6 text-white fill-white ml-0.5" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-bold text-white">{v.title}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-14">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-testid="text-client-wins-heading">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" /> Client Wins & Reviews
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {CLIENT_WINS.map((w, i) => (
              <div key={i} className="flex gap-4 p-5 rounded-2xl bg-[#12121A] border border-[#1E1E2E]" data-testid={`card-client-win-${i}`}>
                <div
                  className="w-20 h-20 rounded-xl shrink-0 flex items-center justify-center bg-gradient-to-br from-[#F6642A] to-[#b8431a] text-white text-2xl font-bold select-none"
                  aria-label={w.name}
                  data-testid={`avatar-client-win-${i}`}
                >
                  {w.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-white">{w.name}</span>
                    <span className="text-xs text-[#F6642A] font-semibold">{w.result}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{w.industry}</p>
                  <p className="text-sm text-gray-300 leading-relaxed line-clamp-3">{w.quote}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-10">
          <Link href="/testimonials">
            <span
              className="text-sm text-[#F6642A] hover:text-[#F6642A]/80 font-semibold transition-colors cursor-pointer"
              data-testid="link-see-all-wins"
            >
              See all client wins &amp; case studies &rarr;
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
